import React from 'react';
import { Link } from 'react-router-dom';
import { Card, Button } from 'react-bootstrap';

import ProviderCredential from '@/website/components/ProviderCredential.tsx';
import { ProviderData } from '@/common/types';
import { useProviderSearchFilters } from '@/common/contexts/ProviderSearchFiltersContext.tsx';
import Avatar from '@/common/components/Avatar.tsx';

interface ProviderCardProps {
  provider: ProviderData;
}

const ProviderCard: React.FC<ProviderCardProps> = ({ provider }) => {
  const { selectedState, selectedInsurance } = useProviderSearchFilters();

  return (
    <Card className="mb-4 shadow-sm">
      <Card.Body>
        <div className="d-flex align-items-center">
          <Avatar
            src={provider.avatarUrl}
            alt={provider.firstName}
            size={130}
            className="me-3"
          />
          <div>
            <Card.Title>
              {provider.firstName} {provider.lastName}
            </Card.Title>
            <Card.Subtitle className="mb-2 text-muted">
              {provider.credentials.map((credential, index) => (
                <span key={credential.id}>
                  <ProviderCredential credential={credential} />
                  {index < provider.credentials.length - 1 && ', '}
                </span>
              ))}
            </Card.Subtitle>
            <div>
              <strong>Licensed States:</strong>
              <div className="mt-1">
                {provider.states.map((state) =>
                  renderBadge(
                    state.code,
                    selectedState === state.code,
                    'bg-secondary'
                  )
                )}
              </div>
            </div>
            <div>
              <strong>Accepted Insurance:</strong>
              <div className="mt-1">
                {provider.insuranceCompanies.map((insurance) =>
                  renderBadge(
                    insurance.name,
                    selectedInsurance === insurance.name,
                    'bg-primary text-white'
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      </Card.Body>
      <Card.Footer>
        <Button
          variant="primary"
          className="me-2"
          onClick={() => {
            if (provider.appointmentSchedulingUrl) {
              window.open(
                provider.appointmentSchedulingUrl,
                '_blank',
                'noopener,noreferrer'
              );
            }
          }}
        >
          Book Appointment
        </Button>
        <Link
          to={`/providers/${provider.id}`}
          state={{
            fromSearch: {
              stateCode: selectedState,
              insuranceCompanyName: selectedInsurance,
            },
          }}
        >
          <Button variant="outline-primary">View Full Profile</Button>
        </Link>
      </Card.Footer>
    </Card>
  );
};

const renderBadge = (
  label: string,
  isHighlighted: boolean,
  baseClass: string = ''
) => {
  const highlightClass = isHighlighted ? 'badge-highlight animate' : baseClass;

  return (
    <span key={label} className={`badge me-1 mb-1 ${highlightClass}`}>
      {label}
    </span>
  );
};

export default ProviderCard;
